import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { ThemeProvider } from './components/ThemeContext';
import { Toaster } from 'sonner';
import './App.css';

// Lazy-loaded page components
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const GenreTracks = lazy(() => import('./pages/GenreTracks'));
const Profile = lazy(() => import('./pages/Profile'));
const Playlists = lazy(() => import('./pages/Playlists'));
const Friends = lazy(() => import('./pages/Friends'));
const Subscription = lazy(() => import('./pages/Subscription'));
const SubscriptionSuccess = lazy(() => import('./pages/SubscriptionSuccess'));
const Discover = lazy(() => import('./pages/Discover'));
const Favorites = lazy(() => import('./pages/Favorites'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute'));

function SuspenseFallback() {
  return (
    <div className="bg-ocean flex flex-col items-center justify-center gap-4">
      <div className="w-10 h-10 border-2 border-white/10 border-t-[#3D7B57] rounded-full animate-spin" />
      <p className="text-[#8FA8A0] text-sm tracking-wide font-['Manrope'] relative z-10">Loading...</p>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return (
      <Suspense fallback={<SuspenseFallback />}>
        <AuthCallback />
      </Suspense>
    );
  }
  
  return (
    <>
      <Suspense fallback={<SuspenseFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/genre/:genreId" element={<ProtectedRoute><GenreTracks /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/playlists" element={<ProtectedRoute><Playlists /></ProtectedRoute>} />
          <Route path="/friends" element={<ProtectedRoute><Friends /></ProtectedRoute>} />
          <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
          <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
          <Route path="/discover" element={<ProtectedRoute><Discover /></ProtectedRoute>} />
          <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
          <Route path="/dev-panel-x9k2" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
      <Toaster
        position="top-center"
        duration={1500}
        toastOptions={{
          style: {
            background: 'rgba(11, 29, 38, 0.55)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            color: '#E4EDE8',
            fontSize: '12px',
            padding: '8px 12px',
            minHeight: 'auto',
            borderRadius: '999px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          },
        }}
      />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <AppRouter />
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;